import React from 'react';
import { Package, User, Eye, Trash2, Tag } from 'lucide-react';
import { SlotWithReservation } from '../types';
import { SlotStatusBadge, PaymentBadge } from './SlotStatusBadge';
import { formatCurrency } from '../utils/currency'; // Issue #905: Decimal.js utilities

interface FairManagerSlotCardProps {
  slot: SlotWithReservation;
  onViewDetails: (slot: SlotWithReservation) => void;
  onDelete: (slot: SlotWithReservation) => void;
  onPriceOverride?: (slot: SlotWithReservation) => void;
}

export default function FairManagerSlotCard({ slot, onViewDetails, onDelete, onPriceOverride }: FairManagerSlotCardProps) {
  return (
    <div className="bg-white shadow-sm rounded-lg p-4" data-testid="slot-card">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <Package className="h-5 w-5 text-gray-400" />
          <span className="font-medium text-gray-900" data-testid="slot-number">{slot.slot_number}</span>
        </div>
        <SlotStatusBadge slot={slot} />
      </div>

      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-500">Type:</span>
          <span className="text-gray-900 capitalize" data-testid="slot-type">
            {slot.slot_type}
            {slot.has_power && <span className="ml-2 text-secondary">⚡</span>}
          </span>
        </div>

        <div className="flex justify-between">
          <span className="text-gray-500">Dimensions:</span>
          <span className="text-gray-900" data-testid="slot-dimensions">{slot.dimensions || 'N/A'}</span>
        </div>

        <div className="flex justify-between">
          <span className="text-gray-500">Price/Day:</span>
          <span className="text-gray-900 font-medium" data-testid="slot-price">{formatCurrency(slot.base_price)} {/* Issue #905: Decimal.js formatting */}</span>
        </div>

        {slot.is_reserved && slot.reservation && (
          <div className="border-t pt-2 mt-2" data-testid="slot-reservation-info">
            <div className="font-medium text-gray-900 mb-1" data-testid="reservation-vendor-name">
              <User className="h-3 w-3 inline mr-1 text-gray-400" />
              {slot.reservation.vendor_name}
            </div>
            <div className="text-xs text-gray-500" data-testid="reservation-vendor-email">{slot.reservation.vendor_email}</div>
            {slot.reservation.vendor_phone && (
              <div className="text-xs text-gray-500" data-testid="reservation-vendor-phone">{slot.reservation.vendor_phone}</div>
            )}
            <div className="mt-1">
              <PaymentBadge paymentStatus={slot.reservation.payment_status} />
            </div>
            <div className="text-xs text-gray-500 mt-1" data-testid="reservation-price-type">
              {formatCurrency(slot.reservation.price_paid)} • {slot.reservation.reservation_type} {/* Issue #905: Decimal.js formatting */}
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-2 mt-4 pt-3 border-t">
        <button
          onClick={() => onViewDetails(slot)}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-primary text-white rounded hover:bg-primary-dark text-sm"
          data-testid="slot-view-details-button"
        >
          <Eye className="h-4 w-4" />
          Details
        </button>
        {onPriceOverride && (
          <button
            onClick={() => onPriceOverride(slot)}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
            data-testid="slot-price-override-button"
          >
            <Tag className="h-4 w-4" />
            Price
          </button>
        )}
        {!slot.is_reserved && (
          <button
            onClick={() => onDelete(slot)}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
            data-testid="slot-card-delete-button"
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </button>
        )}
      </div>
    </div>
  );
}
